/**
 * Investigation subject trail (Case › Order › Payment), not IA hierarchy (OQ-907).
 * Bare 360 pages without case/order context do not invent a trail.
 */
import {
  caseDetailPath,
  displaySubjectRef,
  isInvestigationPath,
  orderInvestigatePath,
  parseSubjectFromLocation,
} from './subjectUrls.js';

export type TrailItem = { href?: string; label: string };

const PATH_LABELS: Record<string, string> = {
  '/case-detail': 'Case',
  '/case-macros': 'Macros',
  '/order-investigation': 'Order',
  '/payment-trace': 'Payment trace',
  '/delivery-timeline': 'Delivery',
  '/issue-refund': 'Refund',
  '/grant-goodwill': 'Goodwill',
  '/waive-fee': 'Waive fee',
  '/create-replacement': 'Replacement',
  '/cancel-order-support': 'Cancel order',
  '/attribute-liability': 'Liability',
  '/remedy-history': 'Remedy history',
  '/escalate': 'Escalate',
  '/four-eyes-confirmation': 'Four-eyes',
  '/refund-offer-composer': 'Refund offer',
  '/delivery-failure-case': 'Delivery failure',
};

export function buildSubjectTrail(pathname: string, searchParams: URLSearchParams): TrailItem[] {
  const subject = parseSubjectFromLocation(pathname, searchParams);
  const caseId = subject.caseId;
  const orderId = subject.orderId;
  const customerId = subject.customerId;
  const businessId = subject.businessId;
  const auditRef = subject.auditRef;

  if (!caseId && !orderId && !customerId && !businessId && !auditRef) return [];
  if (
    !isInvestigationPath(pathname) &&
    !caseId &&
    !orderId &&
    !customerId &&
    !businessId &&
    !auditRef
  ) {
    return [];
  }

  const trail: TrailItem[] = [];

  const isMacrosPath = pathname === '/case-macros' || /\/cases\/[^/]+\/macros\/?$/.test(pathname);

  if (caseId) {
    const href = caseDetailPath(caseId, orderId ? { orderId } : undefined);
    if (subject.leaf === 'case' && !isMacrosPath) {
      trail.push({ label: `Case ${displaySubjectRef(caseId)}` });
    } else {
      trail.push({ href, label: `Case ${displaySubjectRef(caseId)}` });
    }
  }

  if (orderId) {
    const orderHref = orderInvestigatePath(orderId, caseId ? { caseId } : undefined);
    if (subject.leaf === 'order') {
      trail.push({ label: `Order ${displaySubjectRef(orderId)}` });
    } else if (subject.leaf === 'payment' || subject.leaf === 'delivery') {
      trail.push({ href: orderHref, label: `Order ${displaySubjectRef(orderId)}` });
    } else if (pathname === '/order-investigation') {
      trail.push({ label: `Order ${displaySubjectRef(orderId)}` });
    } else {
      trail.push({ href: orderHref, label: `Order ${displaySubjectRef(orderId)}` });
    }
  }

  if (customerId && (subject.leaf === 'customer' || pathname.startsWith('/customers/'))) {
    const already = trail.some((t) => t.label.startsWith('Customer '));
    if (!already) {
      trail.push({
        label: `Customer ${displaySubjectRef(customerId)}`,
      });
    }
  }

  if (businessId && (subject.leaf === 'business' || pathname.startsWith('/businesses/'))) {
    const already = trail.some((t) => t.label.startsWith('Business '));
    if (!already) {
      trail.push({
        label: `Business ${displaySubjectRef(businessId)}`,
      });
    }
  }

  if (auditRef && (subject.leaf === 'audit' || pathname.startsWith('/audit/'))) {
    const already = trail.some((t) => t.label.startsWith('Audit '));
    if (!already) {
      trail.push({
        label: `Audit ${displaySubjectRef(auditRef)}`,
      });
    }
  }

  if (subject.leaf === 'payment' || pathname === '/payment-trace') {
    replaceOrPush(trail, 'Payment trace');
  } else if (subject.leaf === 'delivery' || pathname === '/delivery-timeline') {
    replaceOrPush(trail, 'Delivery');
  } else if (isMacrosPath) {
    replaceOrPush(trail, 'Macros');
  } else {
    const pageLabel = PATH_LABELS[pathname];
    if (
      pageLabel &&
      pathname !== '/order-investigation' &&
      !(pathname === '/case-detail' && caseId) &&
      !pathname.startsWith('/cases/') &&
      !pathname.startsWith('/orders/')
    ) {
      const already = trail.some((t) => t.label === pageLabel || t.label.startsWith(pageLabel));
      if (!already) trail.push({ label: pageLabel });
    }
  }

  return trail.length > 0 ? trail : [];
}

function replaceOrPush(trail: TrailItem[], label: string) {
  const last = trail[trail.length - 1];
  if (last && !last.href) {
    last.label = label;
  } else {
    trail.push({ label });
  }
}
